/* eslint-disable react/no-array-index-key */
import emojiRegex from 'emoji-regex';
import { motion } from 'motion/react';
import { useRef } from 'react';

import type { Variants } from 'motion/react';
import type { HTMLAttributes, Key, RefObject } from 'react';

function throttle(func: (...args: never[]) => void, limit: number) {
  let lastCall = 0;
  return (...args: never[]) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func(...args);
    }
  };
}

/**
 * Splits a string into an array of characters, preserving emojis as single elements.
 * @param str The input string to split.
 * @returns An array of characters and emojis.
 */
function splitStringToCharactersAndEmoji(str: string): string[] {
  const regex = emojiRegex();
  const result: string[] = [];
  let lastIndex = 0;

  str.replace(regex, (match, offset) => {
    if (offset > lastIndex) {
      result.push(...str.slice(lastIndex, offset).split(''));
    }
    result.push(match);
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < str.length) {
    result.push(...str.slice(lastIndex).split(''));
  }

  return result;
}

/**
 * Splits a string into an array of words and emojis, preserving emojis as single elements, entering a space character in between every word so that the spacing is consistent.
 * @param str The input string to split.
 * @returns An array of words and emojis.
 */
function splitStringToWordsAndEmoji(str: string): string[] {
  const regex = emojiRegex();
  const result: string[] = [];
  let lastIndex = 0;

  str.replace(regex, (match, offset) => {
    if (offset > lastIndex) {
      const textBeforeEmoji = str.slice(lastIndex, offset);
      const words = textBeforeEmoji.split(/\s+/).filter((word) => word.length > 0);
      words.forEach((word, index) => {
        result.push(word);
        if (index < words.length - 1) {
          result.push(' ');
        }
      });
      if (words.length > 0) {
        result.push(' ');
      }
    }
    result.push(match);
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < str.length) {
    const remainingText = str.slice(lastIndex);
    const words = remainingText.split(/\s+/).filter((word) => word.length > 0);
    if (result.length > 0 && words.length > 0) {
      result.push(' ');
    }
    words.forEach((word, index) => {
      result.push(word);
      if (index < words.length - 1) {
        result.push(' ');
      }
    });
  }

  return result;
}

type I18NextChildren = HTMLAttributes<HTMLElement>['children'] | Iterable<HTMLAttributes<HTMLElement>['children']>;

interface TypewriterItemProps {
  children?: I18NextChildren;
  className?: string;
  characterVariants: Variants;
  parent: (children?: I18NextChildren, isString?: boolean) => I18NextChildren;
  onCharacterAnimationComplete?: (letterRef: RefObject<HTMLSpanElement | null>) => void;
  splitMethod?: 'characters' | 'words';
  key?: Key | null | undefined;
}

export function TypewriterItem({
  children,
  className,
  characterVariants,
  parent,
  onCharacterAnimationComplete,
  key,
  splitMethod = 'words',
}: TypewriterItemProps): I18NextChildren {
  if (typeof children === 'string') {
    // check if is emoji

    const spanList = (
      splitMethod === 'words' ? splitStringToWordsAndEmoji(children) : splitStringToCharactersAndEmoji(children)
    ).map((char, i) => {
      const ref = useRef<HTMLSpanElement>(null);
      const onAnimationComplete = onCharacterAnimationComplete
        ? throttle(() => onCharacterAnimationComplete(ref), 10)
        : undefined;

      return (
        <motion.span
          ref={ref}
          className={className}
          key={`span-${key}-${char}-${i}`}
          variants={characterVariants}
          onAnimationComplete={onAnimationComplete}
        >
          {char}
        </motion.span>
      );
    });
    return parent(spanList, true);
  }
  if (Array.isArray(children)) {
    const list = children.map((child) => {
      if (typeof child === 'string') {
        const spanList = (child as string).split('').map((char, i) => {
          const ref = useRef<HTMLSpanElement>(null);
          const onAnimationComplete = onCharacterAnimationComplete
            ? throttle(() => onCharacterAnimationComplete(ref), 10)
            : undefined;
          return (
            <motion.span
              ref={ref}
              className={className}
              key={`span-${key}-${char}-${i}`}
              variants={characterVariants}
              onAnimationComplete={onAnimationComplete}
            >
              {char}
            </motion.span>
          );
        });
        return spanList;
      }
      return child;
    });
    return parent(list.flat());
  }
  return parent(children, true);
}
